import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  ToggleButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { InfoOutline } from "@mui/icons-material";
import HPInput from "../../../common/Input";
import IOSSwitch from "../../../common/Switch";
import ApplyColorToAll from "../../ApplyColorToAll";

const PALETTE_PREVIEW: Record<string, string> = {
  Multicolor:
    "linear-gradient(90deg, #ffb09c 0%, #57dfbe 25%, #757679 50%, #a1a7df 75%, #c36179 100%)",
  Gradient: "linear-gradient(90deg, #019EDB 0%, #00CF9C 100%)",
  Grayscale: "linear-gradient(90deg, #f0f0f0 0%, #404040 100%)",
  "Custom (per-slice)":
    "linear-gradient(90deg, #e57373 0%, #81c784 33%, #64b5f6 66%, #ffd54f 100%)",
};

interface SliceColorEntry {
  name: string;
  fallbackColor: string;
  displayName?: string;
  isRollup?: boolean;
}

interface StylingSectionProps {
  title: string;
  subtitle: string;
  titleBold?: boolean;
  titleUnderline?: boolean;
  colorPalette: string;
  legendEnabled: boolean;
  legendLockedBySliceN?: boolean;
  percentageFormatting: number;
  subOnePercentStyle: "<1%" | "0%";
  dataLabelsPosition: "inside" | "outside" | "none";
  sliceValueFormat: "percentage" | "percentage_text";
  minSliceTextThreshold: number;
  forceShowAllValues: boolean;
  truncateCategoryText: boolean;
  // Per-slice color props (only used when colorPalette === "Custom (per-slice)")
  sliceColorEntries?: SliceColorEntry[];
  manualColors?: Record<string, string>;
  onManualColorChange?: (sliceName: string, color: string | null) => void;
  onApplyColorToAll?: (color: string) => void;
  onTitleChange: (value: string) => void;
  onSubtitleChange: (value: string) => void;
  onTitleBoldChange: (value: boolean) => void;
  onTitleUnderlineChange: (value: boolean) => void;
  onColorPaletteChange: (value: string) => void;
  onLegendEnabledChange: (value: boolean) => void;
  onPercentageFormattingChange: (value: number) => void;
  onSubOnePercentStyleChange: (value: "<1%" | "0%") => void;
  onDataLabelsPositionChange: (value: "inside" | "outside" | "none") => void;
  onSliceValueFormatChange: (value: "percentage" | "percentage_text") => void;
  onMinSliceTextThresholdChange: (value: number) => void;
  onForceShowAllValuesChange: (value: boolean) => void;
  onTruncateCategoryTextChange: (value: boolean) => void;
}

const toggleButtonSx = {
  width: 32,
  height: 32,
  minWidth: 32,
  border: "1px solid #e0e0e0",
  "&.Mui-selected": {
    backgroundColor: "#1976d2",
    color: "white",
    "&:hover": { backgroundColor: "#1565c0" },
  },
};

const StylingSection: React.FC<StylingSectionProps> = ({
  title,
  subtitle,
  titleBold,
  titleUnderline,
  colorPalette,
  legendEnabled,
  legendLockedBySliceN = false,
  percentageFormatting,
  subOnePercentStyle,
  dataLabelsPosition,
  sliceValueFormat,
  minSliceTextThreshold,
  forceShowAllValues,
  truncateCategoryText,
  sliceColorEntries,
  manualColors,
  onManualColorChange,
  onApplyColorToAll,
  onTitleChange,
  onSubtitleChange,
  onTitleBoldChange,
  onTitleUnderlineChange,
  onColorPaletteChange,
  onLegendEnabledChange,
  onPercentageFormattingChange,
  onSubOnePercentStyleChange,
  onDataLabelsPositionChange,
  onSliceValueFormatChange,
  onMinSliceTextThresholdChange,
  onForceShowAllValuesChange,
  onTruncateCategoryTextChange,
}) => {
  // Tracks the manualColor value (or null) each slice had BEFORE the user
  // started the current edit session — same pattern as Bar Chart's per-bar colors.
  const [pendingOriginals, setPendingOriginals] = useState<
    Record<string, string | null>
  >({});

  // Clear pending state when switching away from Custom (per-slice)
  useEffect(() => {
    if (colorPalette !== "Custom (per-slice)") {
      setPendingOriginals({});
    }
  }, [colorPalette]);

  return (
    <Accordion defaultExpanded sx={{ boxShadow: "none", border: "1px solid #eee" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Styling & Customization
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ position: "relative" }}>
            <HPInput
              label="Chart title"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: "70%",
                right: 0,
                display: "flex",
                gap: 0.5,
              }}
            >
              <ToggleButton
                value="bold"
                selected={!!titleBold}
                onChange={() => onTitleBoldChange(!titleBold)}
                size="small"
                sx={toggleButtonSx}
              >
                <FormatBoldIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton
                value="underline"
                selected={!!titleUnderline}
                onChange={() => onTitleUnderlineChange(!titleUnderline)}
                size="small"
                sx={toggleButtonSx}
              >
                <FormatUnderlinedIcon fontSize="small" />
              </ToggleButton>
            </Box>
          </Box>

          <HPInput
            label="Subtitle"
            value={subtitle}
            onChange={(e) => onSubtitleChange(e.target.value)}
            placeholder="Optional"
          />

          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Color Palette
            </FormLabel>
            <Select
              value={colorPalette}
              onChange={(e) => onColorPaletteChange(e.target.value)}
              renderValue={(value) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: "space-between",
                    flexDirection: "row-reverse",
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 12,
                      borderRadius: "16px",
                      background: PALETTE_PREVIEW[value as string] || "#57bfe7",
                    }}
                  />
                  {value}
                </Box>
              )}
            >
              {Object.entries(PALETTE_PREVIEW).map(([name, gradient]) => (
                <MenuItem key={name} value={name}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: gradient,
                      }}
                    />
                    {name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Per-slice color pickers (Custom per-slice palette only) */}
          {colorPalette === "Custom (per-slice)" &&
            sliceColorEntries &&
            sliceColorEntries.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {onApplyColorToAll && (
                  <ApplyColorToAll
                    onApply={onApplyColorToAll}
                    defaultColor={
                      manualColors?.[sliceColorEntries[0].name] ||
                      sliceColorEntries[0].fallbackColor
                    }
                    appliedColors={sliceColorEntries.map(
                      (slice) =>
                        manualColors?.[slice.name] || slice.fallbackColor,
                    )}
                  />
                )}
                {sliceColorEntries.map((slice) => {
                  const currentColor =
                    manualColors?.[slice.name] || slice.fallbackColor;
                  // isDirty only while the user has a pending (unconfirmed) change
                  const isDirty = slice.name in pendingOriginals;
                  return (
                    <Box
                      key={slice.name}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "12px",
                          }}
                        >
                          {slice.displayName ?? slice.name}
                        </Typography>
                        {slice.isRollup && (
                          <Chip
                            label="Rollup"
                            size="small"
                            sx={{ fontSize: 10, height: 18, flexShrink: 0 }}
                          />
                        )}
                      </Box>
                      <input
                        type="color"
                        value={currentColor}
                        onChange={(e) => {
                          // Store the original before the first change so Cancel can revert
                          if (!(slice.name in pendingOriginals)) {
                            setPendingOriginals((prev) => ({
                              ...prev,
                              [slice.name]: manualColors?.[slice.name] ?? null,
                            }));
                          }
                          // Update Redux immediately for real-time chart preview
                          onManualColorChange?.(slice.name, e.target.value);
                        }}
                        style={{
                          width: 32,
                          height: 24,
                          cursor: "pointer",
                          border: isDirty
                            ? "2px solid #1976d2"
                            : "1px solid #ccc",
                          borderRadius: 4,
                          padding: 0,
                        }}
                      />
                      {isDirty && (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            title="Apply color"
                            onClick={() =>
                              setPendingOriginals((prev) => {
                                const next = { ...prev };
                                delete next[slice.name];
                                return next;
                              })
                            }
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Cancel"
                            onClick={() => {
                              const original = pendingOriginals[slice.name];
                              // Revert Redux to what it was before editing
                              onManualColorChange?.(slice.name, original);
                              setPendingOriginals((prev) => {
                                const next = { ...prev };
                                delete next[slice.name];
                                return next;
                              });
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography>Legend</Typography>
              <IOSSwitch
                checked={legendEnabled}
                disabled={legendLockedBySliceN}
                onChange={(e) => onLegendEnabledChange(e.target.checked)}
                size="small"
              />
            </Box>
            {legendLockedBySliceN && (
              <Typography variant="caption" color="text.secondary">
                Locked on because Show Slice N (N Display) is enabled.
              </Typography>
            )}
          </Box>

          <FormControl fullWidth size="small">
            <FormLabel className="text-sm">Rounding</FormLabel>
            <Select
              value={percentageFormatting}
              onChange={(e) =>
                onPercentageFormattingChange(Number(e.target.value))
              }
            >
              <MenuItem value={0}>0 decimals</MenuItem>
              <MenuItem value={1}>1 decimal</MenuItem>
              <MenuItem value={2}>2 decimals</MenuItem>
            </Select>
          </FormControl>

          {percentageFormatting === 0 && (
            <FormControl component="fieldset" fullWidth size="small">
              <FormLabel className="text-sm">Sub‑1% Style</FormLabel>
              <RadioGroup
                value={subOnePercentStyle}
                onChange={(e) =>
                  onSubOnePercentStyleChange(e.target.value as "<1%" | "0%")
                }
                row
              >
                <FormControlLabel
                  value="<1%"
                  control={<Radio size="small" />}
                  label="<1%"
                />
                <FormControlLabel
                  className="ml-26"
                  value="0%"
                  control={<Radio size="small" />}
                  label="0%"
                />
              </RadioGroup>
            </FormControl>
          )}

          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Slice Value and Text placement
            </FormLabel>
            <Select
              value={dataLabelsPosition}
              onChange={(e) =>
                onDataLabelsPositionChange(
                  e.target.value as "inside" | "outside" | "none",
                )
              }
            >
              <MenuItem value="inside">Inside</MenuItem>
              <MenuItem value="outside">Outside</MenuItem>
              <MenuItem value="none">None</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Slice Value and Text format
            </FormLabel>
            <Select
              value={sliceValueFormat}
              onChange={(e) =>
                onSliceValueFormatChange(
                  e.target.value as "percentage" | "percentage_text",
                )
              }
            >
              <MenuItem value="percentage">Percentage only</MenuItem>
              <MenuItem value="percentage_text">
                Percentage + Short Response Text
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Minimum Slice Value Text Threshold
            </FormLabel>
            <Select
              value={minSliceTextThreshold}
              onChange={(e) =>
                onMinSliceTextThresholdChange(Number(e.target.value))
              }
            >
              <MenuItem value={1}>1%</MenuItem>
              <MenuItem value={2}>2%</MenuItem>
              <MenuItem value={3}>3%</MenuItem>
              <MenuItem value={4}>4%</MenuItem>
              <MenuItem value={5}>5%</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IOSSwitch
              checked={forceShowAllValues}
              onChange={(e) => onForceShowAllValuesChange(e.target.checked)}
              size="small"
            />
            <Typography>Force Show All Values</Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography>Truncate category text</Typography>
              <Tooltip title="Truncate slice names at 40 characters in the legend and outside labels">
                <InfoOutline sx={{ fontSize: 16, color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <IOSSwitch
              checked={truncateCategoryText}
              onChange={(e) => onTruncateCategoryTextChange(e.target.checked)}
              size="small"
            />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default StylingSection;
